import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { LIST_MUTED_COLOR } from "components/list/ListTable";
import { numberToHour, SAUVEGARDE_LABELS, sauvegardeHorsAkeeba } from "utils";
import { toDate } from "views/admin/default/dashboardData";
import { EN_LIGNE_LABELS, SAUVEGARDE_ETAT_LABELS } from "views/admin/maintenance/libelles";
import { detailDuSite, enLigneDuSite, etatDeLaTache, tropAncien } from "views/admin/maintenance/suivi";

const FREQUENCE_LABELS: Record<string, string> = {
  quotidienne: "Quotidienne",
  quotidien: "Quotidienne",
  hebdomadaire: "Hebdomadaire",
  hebdo: "Hebdomadaire",
  mensuelle: "Mensuelle",
  mensuel: "Mensuelle",
};
const dateHeure = (date?: Date | null) => (date ? moment(date).format("DD/MM/YYYY à HH:mm") : "");

function Rubrique(props: { titre: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1}>
      <Heading as="h3" size="xs">
        {props.titre}
      </Heading>
      {props.children}
    </Stack>
  );
}

const nonCollecte = (
  <Text fontSize="sm" color={LIST_MUTED_COLOR}>
    Non collecté
  </Text>
);

// Panneau de détail d'un site, ouvert depuis la liste
export default function SiteDetail(props: {
  site: (SiteType & { client: string }) | null;
  etat?: SiteEtatType;
  forfaits: ForfaitType[];
  interventions: InterventionType[];
  onClose: () => void;
  onModifier: () => void;
}) {
  const { site } = props;
  const detail = site ? detailDuSite(site.id, props.forfaits, props.interventions) : null;
  const maintenant = new Date();
  const collecte = props.etat && !tropAncien(props.etat.collecte, maintenant) ? props.etat : undefined;
  const taches = collecte?.sauvegardes.filter((tache) => tache.actif) ?? [];
  const enLigne = enLigneDuSite(props.etat, maintenant);
  const controle = props.etat?.disponibilite?.dernier_controle;
  return (
    <Drawer isOpen={Boolean(site)} onClose={props.onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={1}>
          {site?.nom}
          <Text fontSize="sm" fontWeight="400" color={LIST_MUTED_COLOR}>
            {site?.client}
          </Text>
        </DrawerHeader>
        <DrawerBody>
          <Stack spacing={5}>
            <Link href={site?.url} isExternal fontSize="sm">
              {site?.url}
            </Link>
            <Rubrique titre="Plugins et thèmes">{nonCollecte}</Rubrique>
            <Rubrique titre="Tâches de sauvegarde">
              {site && sauvegardeHorsAkeeba(site) ? (
                <Text fontSize="sm" color={LIST_MUTED_COLOR}>
                  {SAUVEGARDE_LABELS[site.sauvegarde]}
                </Text>
              ) : !collecte ? (
                nonCollecte
              ) : taches.length ? (
                taches.map((tache, index) => {
                  const resultat = etatDeLaTache(tache, maintenant);
                  return (
                    <Stack key={index} spacing={0}>
                      <Text fontSize="sm">
                        {FREQUENCE_LABELS[String(tache.frequence).toLowerCase()] ?? tache.frequence}
                        {tache.profil ? ` (profil ${tache.profil})` : ""} : {SAUVEGARDE_ETAT_LABELS[resultat.etat]}
                        {tache.dernier_succes ? ` · dernier succès le ${dateHeure(tache.dernier_succes)}` : ""}
                      </Text>
                      {resultat.etat !== "ok" && tache.message && (
                        <Text fontSize="xs" color={LIST_MUTED_COLOR}>
                          {tache.message}
                        </Text>
                      )}
                    </Stack>
                  );
                })
              ) : (
                <Text fontSize="sm">Aucune tâche Akeeba configurée</Text>
              )}
            </Rubrique>
            <Rubrique titre="Disponibilité">
              {enLigne.etat === "non_collecte" ? (
                nonCollecte
              ) : (
                <>
                  <Text fontSize="sm">
                    {EN_LIGNE_LABELS[enLigne.etat]}
                    {enLigne.depuis ? ` depuis le ${dateHeure(enLigne.depuis)}` : ""}
                    {enLigne.message ? ` · ${enLigne.message}` : ""}
                  </Text>
                  <Text fontSize="xs" color={LIST_MUTED_COLOR}>
                    Contrôlé le {dateHeure(controle)}
                  </Text>
                </>
              )}
            </Rubrique>
            <Rubrique titre="Forfaits actifs">
              {detail?.forfaits.length ? (
                detail.forfaits.map(({ forfait, restant }) => (
                  <Text key={forfait.id} fontSize="sm">
                    {restant === undefined
                      ? "Annuel, sans suivi d'interventions"
                      : `Horaire : ${numberToHour(restant)} restantes sur ${numberToHour(forfait.temps)}`}
                  </Text>
                ))
              ) : (
                <Text fontSize="sm" color={LIST_MUTED_COLOR}>
                  Aucun
                </Text>
              )}
            </Rubrique>
            <Rubrique titre="Interventions récentes">
              {detail?.interventions.length ? (
                detail.interventions.map((intervention) => (
                  <Text key={intervention.id} fontSize="sm">
                    {moment(toDate(intervention.date_intervention)).format("DD/MM/YYYY")} · {numberToHour(intervention.temps_passe)}
                    {intervention.plugins && " · plugins"}
                    {intervention.sauvegarde && " · sauvegarde"}
                  </Text>
                ))
              ) : (
                <Text fontSize="sm" color={LIST_MUTED_COLOR}>
                  Aucune
                </Text>
              )}
            </Rubrique>
          </Stack>
        </DrawerBody>
        <DrawerFooter>
          <Button size="sm" variant="outline" onClick={props.onModifier}>
            Modifier le site
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
